import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, forkJoin, map, Observable, of } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { ApiConstants } from 'src/app/base/api-constants';

import { getAllQueries, getQBFields, setQBProperties } from 'src/app/STORE/actions/queryBuilder.actions';
import { getIdMapping } from 'src/app/STORE/actions/screens.actions';
import { getFields } from 'src/app/STORE/actions/services.actions';
import { allQueries, qbFields } from 'src/app/STORE/selectors/queryBuilder.selector';
import { allIdMaps } from 'src/app/STORE/selectors/screens.selector';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { metaInfo } from 'src/app/STORE/selectors/applifecycle.selector';
import { UtilService } from 'src/app/services/util.service';
import { AllowedFieldsConstants } from 'src/app/base/allowed-fields.constants';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { technologystackData } from 'src/app/STORE/selectors/technologystack.selector';
import { GlobalConstants } from 'src/app/base/global.constants';
import { AppGlobalService } from 'src/app/services/app.global.service';

export enum Operators {
  ISEQUAL = 'is equal to',
  ISNOTEQUAL = 'is not equal to',
  ISEMPTY = 'is empty',
  ISNOTEMPTY = 'is not empty',
  ISIN = 'is in',
  ISNOTIN = 'is not in',
  HASANYOF = 'has any of',
  ISBEFORE = 'is before',
  ISAFTER = 'is after',
  ISEQUALORBEFORE = 'is equal or before',
  ISEQUALORLATERTHAN = 'is equal or later than',
  HASNONEOF = 'has none of',
  ISLESSTHAN = 'is less than',
  ISGREATERTHAN = 'is greater than',
  ISLESSTHANOREQUALTO = 'is less than or equal to',
  ISGREATERTHANOREQUALTO = 'is greater than or equal to',
  ISBETWEEN = 'is between',
  ISNOTBETWEEN = 'is not between',
  HASALLOF = 'has all of'
}

@Injectable({
  providedIn: 'root'
})
export class VsQueryBuilderService {
  private store = inject(Store);
  private loader = inject(AppLoaderService);
  private util = inject(UtilService);
  private bs = inject(HttpBaseService);
  private appGlobalService = inject(AppGlobalService);

  appUserTableId: any;
  appUserAppId: any = null;
  currentTableId: any;
  currentRefAppId: any = null;
  currentTableName: any;
  currentField: any;
  fieldIdMap: any;
  tableIdMap: any;
  qbFields;
  fields;
  roles;
  isRolesNeeded: any = true;
  isAppUserNeeded: any = true;
  isCurrentTableNeeded = true;
  isDefaultFilter = false;
  currScr: { name?: string } | null = null;

  // List of allowed operators for CUDConditional
  static allowedCudOperators = [
    Operators.ISEQUAL,
    Operators.ISNOTEQUAL,
    Operators.ISEMPTY,
    Operators.ISNOTEMPTY,
    Operators.ISBEFORE,
    Operators.ISAFTER,
    Operators.ISEQUALORBEFORE,
    Operators.ISEQUALORLATERTHAN,
    Operators.ISLESSTHAN,
    Operators.ISGREATERTHAN,
    Operators.ISLESSTHANOREQUALTO,
    Operators.ISGREATERTHANOREQUALTO,
    Operators.ISIN,
    Operators.ISNOTIN,
    Operators.HASANYOF,
    Operators.HASNONEOF
  ];
  // Holds context info (both current and parent context table ids)
  qbContextTableInScreen: { currentContextTableId?: string; parentContextTableId?: string } = {};

  constructor() {
    this.store.dispatch(getAllQueries({ apiConstants: ApiConstants.getAllQueriesByAppId }));
    this.store
      .select(qbFields)
      .pipe(distinctUntilChanged())
      .subscribe((res: any) => {
        let indexedFields: any[] = [];
        let searchableFields: any[] = [];
        const response = cloneDeep(res);
        // If currentTableId is present, proceed with the API calls
        if (this.currentTableId) {
          // const indexAndSearchInfo$ = this.getIndexOrSearchableFields();

          this.getIndexOrSearchableFields().subscribe({
            next: ({ indexData, searchableFieldsData, currentTableInfo }) => {
              // Process indexData
              if (Array.isArray(indexData)) {
                indexedFields = indexData.flatMap((item: any) => item?.fields || []);
              }

              // for default filter, show the referred table's searchable fields also in the list
              if (currentTableInfo && currentTableInfo.currentTableSearchableField) {
                // process searchable fields data and  referred table fields from the foreign keys
                const fieldsInfo = this.util.getSearchableReferredTableFields(currentTableInfo, this.currentTableId);
                if (fieldsInfo && fieldsInfo.searchableFields) {
                  searchableFields = fieldsInfo.searchableFields;
                }
                if (fieldsInfo && fieldsInfo.referredTableFields) {
                  response[0] = [...response[0], ...fieldsInfo.referredTableFields];
                }
              }

              // Continue with processing response
              this.processResult(response, indexedFields, searchableFields);
            },
            error: (err) => {
              console.error('Error fetching index or searchable fields:', err);
              // Continue with processing res if needed
              this.processResult(res, indexedFields, searchableFields);
            }
          });
        } else {
          // If currentTableId is missing, skip the API calls and proceed with the available res data
          this.processResult(res, indexedFields, searchableFields);
        }
      });
  }

  private getIndexOrSearchableFields(): Observable<any> {
    const techStack = this.appGlobalService.get('technologyChoices');
    const appId = this.appGlobalService.get('appId');
    const orgId = this.appGlobalService.get('orgId');
    if (techStack.textSearch === GlobalConstants.searchDbEs) {
      return forkJoin({
        indexData: of([]),
        searchableFieldsData: this.bs.get(ApiConstants.getSearchableIndexFields, { tableId: this.currentTableId }),
        currentTableInfo: this.bs.get(ApiConstants.getSearchableFieldsByTableId, { tableId: this.currentTableId })
      }).pipe(
        map((response) => ({
          indexData: response.indexData || [],
          searchableFieldsData: Array.isArray(response.searchableFieldsData)
            ? response.searchableFieldsData.map((field: any) => field.fieldSid)
            : [],
          currentTableInfo: response.currentTableInfo || null
        }))
      );
    } else {
      return forkJoin({
        indexData: this.bs.get(ApiConstants.getIndex, { appId: appId, orgId: orgId, tableId: this.currentTableId }),
        searchableFieldsData: of(null)
      }).pipe(
        map((response) => ({
          indexData: response.indexData || [],
          searchableFieldsData: [],
          currentTableInfo: []
        }))
      );
    }
  }

  // Set context (store both current and parent context table ids)
  setQueryBuilderContext(ctx: { currentContextTableId?: string; parentContextTableId?: string }) {
    this.qbContextTableInScreen = ctx || {};
  }

  // Clear previously set context
  clearQueryBuilderContext() {
    this.qbContextTableInScreen = {};
  }

  processResult(res: any, indexedFields: string[], searchableFields: string[]) {
    if (res) {
      try {
        // Clone each object in res[0] before adding the 'searchable' and 'indexed' properties
        const modifiedRes = res[0].map((obj) => {
          const clonedObj = { ...obj }; // Shallow copy to avoid mutating the original object
          clonedObj.searchable = searchableFields.includes(obj.sid);
          clonedObj.indexed = indexedFields.includes(obj.sid); // Add searchable and indexed properties
          return clonedObj;
        });

        // Handle the response based on role and user needs
        if (!this.isRolesNeeded && !this.isAppUserNeeded) {
          this.configureQBProperties(modifiedRes, null, null);
        } else if (!this.isRolesNeeded && this.isAppUserNeeded) {
          this.configureQBProperties(modifiedRes, res[1], null);
        } else {
          this.configureQBProperties(modifiedRes, res[1], res[2]?.results);
        }
      } catch (e) {
        console.error('Error processing result:', e);
      }
    }

    const idsSub = this.store.select(metaInfo).subscribe((res: any) => {
      if (res) {
        // if (typeof res.fieldsMap==='string') {
        //   this.fieldIdMap = JSON.parse(res.fieldsMap);
        //   this.tableIdMap = JSON.parse(res.tablesMap);
        // } else {
        //   if(res.fieldsMap && Object.keys(res.fieldsMap).length) {
        //     this.fieldIdMap = res.fieldsMap;
        //   }
        //   if(res.tablesMap && Object.keys(res.tablesMap).length) {
        //     this.tableIdMap = res.tablesMap;
        //   }
        // }
        this.fieldIdMap = res;
        this.tableIdMap = res;
      }
    });
  }

  getFields(id) {
    if (id) {
      this.store.dispatch(
        getFields({
          serviceAPiConstants: ApiConstants.getFieldInfo,
          params: { tableId: id }
        })
      );
    }
  }

  configureQueryBuilder(appUserTableId, tableId, tableName, fieldId?,refAppId?) {
    this.appUserTableId = appUserTableId;
    this.currentTableId = tableId;
    if(refAppId && refAppId != this.appGlobalService.get('appId')){
    this.currentRefAppId = refAppId;
    }
    else{
      this.currentRefAppId = null;
    }
    this.currentTableName = tableName;
    if (!this.currentTableName) {
      this.currentTableName = this.tableIdMap[this.currentTableId];
    }
    this.currentField = fieldId;
    /* this.store.dispatch(setQBQueries({ qbQueries: null }));
    this.store.dispatch(getQBQueries({
      apiConstants: ApiConstants.getQueriesListByTableId,
      params: { tableId: this.currentTableId }
    })); */
  }

  configureQBFields(obj: any) {
    this.isRolesNeeded = obj.roleNeeded;
    this.isAppUserNeeded = obj.isAppUserNeeded;
    this.isCurrentTableNeeded = obj.isCurrentTableNeeded;
    this.isDefaultFilter = obj.isDefaultFilter;
    this.currScr = obj.currScr;
    this.store.dispatch(setQBProperties({ qbProperties: null }));
    try {
      const appUserApiConstants = { ...ApiConstants.getFields };
      appUserApiConstants.url = ApiConstants.getFields.url + this.appUserTableId;

      const currentTableApiConstants = { ...ApiConstants.getFields };
      currentTableApiConstants.url = ApiConstants.getFields.url + this.currentTableId;
      if(this.currentRefAppId){
        currentTableApiConstants.url = currentTableApiConstants.url + "/" + this.currentRefAppId;
      }
      const apiConstants: any = {
        currentTable: currentTableApiConstants
      };
      if (this.isAppUserNeeded) {
        apiConstants.appUser = appUserApiConstants;
      }
      if (this.isRolesNeeded) {
        apiConstants.roles = ApiConstants.getRolesData;
      }

      this.store.dispatch(
        getQBFields({
          apiConstants
        })
      );
    } catch (e) {
      console.error(e);
    }
  }

  removeIncompatableFieldType(fields) {
    if (this.isDefaultFilter) {
      return fields.filter(
        (field: any) =>
          field.name.toLowerCase() !== 'sid' &&
          field.name.toLowerCase() !== 'sync time' &&
          field.name.toLowerCase() !== 'pid'
      );
    }
    return fields.filter((field: any) => {
      return (
        field &&
        field.f_type !== 'File Attachments' &&
        field.f_type !== 'Image Carousel' &&
        field.f_type !== 'Image' &&
        field.f_type !== 'JSON'
      );
    });
  }

  clearQueryBuilder() {
    /* empty */
  }

  configureQBProperties(currentTableFields, appUserFields, roles) {
    const qbFields = {};
    const entities = {};
    let entityList = [];
    if (this.isAppUserNeeded) {
      // Store appUserAppId from first field for reference creation
      if (appUserFields?.length > 0 && appUserFields[0]?.appId) {
        this.appUserAppId = appUserFields[0].appId;
      }
      const currentUserFields: any = [];
      /* currentUserFields.unshift(roleField); */
      appUserFields.forEach((e) => {
        const obj: any = { ...e };
        obj.f_tableId = `currentUser-${this.appUserTableId}`;
        obj.sid = `currentUser-${e.sid}`;
        if (e.name === 'User Roles') {
          obj.name = 'Role';
          obj.f_type = 'role';
          // obj.sid=e.sid;
          if (this.isRolesNeeded) {
            currentUserFields.unshift(obj);
          }
        } else {
          currentUserFields.push(obj);
        }
      });
      this.configureQueryBuilderProperties(
        qbFields,
        'Current User',
        currentUserFields,
        entities,
        entityList,
        roles
      );
    }
    if (this.isCurrentTableNeeded) {
      this.configureQueryBuilderProperties(
        qbFields,
        this.currentTableName,
        currentTableFields,
        entities,
        entityList
      );
    }
    entityList = [...entityList.filter((e) => e['name'] != 'Application User')];
    let qbPropertiesUpdate = {
      entities: entities,
      entityList: entityList,
      fields: qbFields
    };
    console.log(qbPropertiesUpdate);
    this.store.dispatch(setQBProperties({ qbProperties: qbPropertiesUpdate }));
  }

  configureQueryBuilderProperties(qbFields: any, tableName, fields: any, entities, entityList, roles?: any) {
    if (fields) {
      const tableFields = [];
      this.formQueryBuilderProperties(fields, tableFields, qbFields, roles);
      if (fields.length) {
        const tableId = fields[0].f_tableId;
        entities[tableId] = { name: tableName };
        entityList.push({ name: tableName, value: tableId });
      }
    }
  }

  formQueryBuilderProperties(fields, qbProperties, qbFields, roles?: any) {
    fields.forEach((field) => {
      if (!field.name) return;
      let type = field.f_type.toLowerCase();
      let qbType = '';
      let fieldObj: any = { name: field.name, sid: field.sid };
      let cudOperators = this.currScr?.name === 'CUD' ? VsQueryBuilderService.allowedCudOperators : undefined;
      const setOperators = (ops: any[]) => (cudOperators ? ops.filter((op) => cudOperators.includes(op)) : ops);
      switch (type) {
        case 'role':
          qbType = 'role';
          fieldObj.nullable = true;
          fieldObj.operators = setOperators([Operators.HASANYOF, Operators.HASNONEOF, Operators.HASALLOF]);
          fieldObj.options = roles ? roles.map((r: any) => ({ name: r.shortName, value: r.sid })) : [];
          break;
        case 'dropdown':
        case 'radio':
        case 'checkboxgroup':
          if (field.f_aValues) {
            qbType = 'category';
            const allowedValues = JSON.parse(field.f_aValues)?.values || [];
            fieldObj.options = allowedValues.map((v: any) => ({
              name: v?.name,
              value: v?.sid
            }));
            fieldObj.operators = setOperators([
              Operators.HASANYOF,
              Operators.HASNONEOF,
              Operators.HASALLOF,
              Operators.ISEMPTY,
              Operators.ISNOTEMPTY,
              Operators.ISEQUAL,
              Operators.ISNOTEQUAL
            ]);
          } else {
            qbType = 'string';
            fieldObj.operators = setOperators(
              field.f_hasMultiVal
                ? [Operators.ISEMPTY, Operators.ISNOTEMPTY, Operators.HASANYOF, Operators.HASNONEOF, Operators.HASALLOF]
                : [
                    Operators.ISEQUAL,
                    Operators.ISNOTEQUAL,
                    Operators.ISEMPTY,
                    Operators.ISNOTEMPTY,
                    Operators.ISIN,
                    Operators.ISNOTIN
                  ]
            );
          }
          if (this.isDefaultFilter) {
            qbType = 'category';
            fieldObj.operators = [Operators.ISEQUAL, Operators.HASANYOF, Operators.ISEMPTY];
          }
          break;
        case 'date':
          qbType = 'date';
          if (!this.isDefaultFilter) fieldObj.defaultValue = () => new Date();
          if (this.isDefaultFilter) {
            fieldObj.options = [
              { name: 'Yesterday', value: 'yesterday' },
              { name: 'Today', value: 'today' },
              { name: 'Tomorrow', value: 'tomorrow' }
            ];
            fieldObj.operators = [Operators.ISEQUAL, Operators.ISBETWEEN, Operators.ISEMPTY];
          } else {
            fieldObj.operators = setOperators([
              Operators.ISEQUAL,
              Operators.ISBEFORE,
              Operators.ISAFTER,
              Operators.ISEQUALORBEFORE,
              Operators.ISEQUALORLATERTHAN,
              Operators.ISEMPTY,
              Operators.ISNOTEMPTY
            ]).concat(cudOperators ? [] : [Operators.ISBETWEEN, Operators.ISNOTBETWEEN]);
          }
          break;
        case 'datetime':
          qbType = 'time';
          fieldObj.defaultValue = () => new Date();
          if (this.isDefaultFilter) {
            fieldObj.options = [
              { name: 'Yesterday', value: 'yesterday' },
              { name: 'Today', value: 'today' },
              { name: 'Tomorrow', value: 'tomorrow' }
            ];
            fieldObj.operators = [Operators.ISEQUAL, Operators.ISBETWEEN, Operators.ISEMPTY];
          } else {
            fieldObj.operators = setOperators([
              Operators.ISEQUAL,
              Operators.ISBEFORE,
              Operators.ISAFTER,
              Operators.ISEQUALORBEFORE,
              Operators.ISEQUALORLATERTHAN,
              Operators.ISEMPTY,
              Operators.ISNOTEMPTY
            ]).concat(cudOperators ? [] : [Operators.ISBETWEEN, Operators.ISNOTBETWEEN]);
          }
          break;
        case 'number':
        case 'currency':
        case 'double':
          qbType = type === 'number' ? 'number' : 'double';
          fieldObj.operators = this.isDefaultFilter
            ? [Operators.ISEQUAL, Operators.ISEMPTY, Operators.ISBETWEEN]
            : setOperators([
                Operators.ISEQUAL,
                Operators.ISNOTEQUAL,
                Operators.ISLESSTHAN,
                Operators.ISGREATERTHAN,
                Operators.ISLESSTHANOREQUALTO,
                Operators.ISGREATERTHANOREQUALTO
              ]).concat(cudOperators ? [] : [Operators.ISBETWEEN, Operators.ISNOTBETWEEN]);
          break;
        case 'boolean':
        case 'checkbox':
          qbType = 'boolean';
          fieldObj.options = [
            { name: 'True', value: true },
            { name: 'False', value: false }
          ];
          fieldObj.operators = this.isDefaultFilter
            ? [Operators.ISEQUAL]
            : setOperators([Operators.ISEQUAL, Operators.ISNOTEQUAL]);
          break;
        case 'yes no':
          qbType = 'yes no';
          if (this.isDefaultFilter) {
            fieldObj.operators = [Operators.ISEQUAL];
            fieldObj.options = [
              { name: 'Yes', value: 'YES' },
              { name: 'No', value: 'NO' }
            ];
          } else {
            fieldObj.operators = setOperators([Operators.ISEQUAL, Operators.ISNOTEQUAL]);
            fieldObj.options = [
              { name: 'Yes', value: 'yes' },
              { name: 'No', value: 'no' }
            ];
          }
          break;
        case 'lookup':
          qbType = 'lookup';
          fieldObj.nullable = true;
          fieldObj.operators = setOperators(
            field.f_hasMultiVal
              ? [Operators.ISEMPTY, Operators.ISNOTEMPTY, Operators.HASANYOF, Operators.HASNONEOF, Operators.HASALLOF]
              : [Operators.ISEQUAL, Operators.ISNOTEQUAL, Operators.ISEMPTY, Operators.ISNOTEMPTY]
          );
          break;
        default:
          qbType = type === 'email' ? 'email' : type === 'auto-number' ? 'auto-number' : 'string';
          if (this.isDefaultFilter) {
            if (AllowedFieldsConstants.defaultFilter['f_type'].includes(type)) {
              fieldObj.operators = [];
              fieldObj.defaultOperator = null;
            } else {
              fieldObj.operators = [Operators.ISEQUAL, Operators.ISEMPTY];
              fieldObj.defaultOperator = Operators.ISEQUAL;
            }
          } else {
            fieldObj.operators = setOperators(
              field.f_hasMultiVal
                ? [Operators.ISEMPTY, Operators.ISNOTEMPTY, Operators.HASANYOF, Operators.HASNONEOF, Operators.HASALLOF]
                : [
                    Operators.ISEQUAL,
                    Operators.ISNOTEQUAL,
                    Operators.ISEMPTY,
                    Operators.ISNOTEMPTY,
                    Operators.ISIN,
                    Operators.ISNOTIN
                  ]
            );
          }
          fieldObj.nullable = true;
      }
      if (qbType) type = qbType;
      let resultField: any = {
        ...field,
        ...fieldObj,
        type,
        entity: field.f_tableId,
        f_hasMultiVal: field.f_hasMultiVal,
        searchable: field.searchable
      };
      if (this.isDefaultFilter) {
        resultField = { ...field, ...fieldObj, type, entity: field.f_tableId };
        const fldCons = AllowedFieldsConstants.defaultFilter;
        const techStack = this.appGlobalService.get('technologyChoices');
        if (
          fldCons['f_type'].includes(field?.f_type?.toLowerCase()) ||
          (fldCons['searchable'] != resultField.searchable && techStack.textSearch != 'RDBMS') ||
          fldCons['f_transientField'] == field.f_transientField
        ) {
          resultField = { ...resultField, disabled: true };
        }
      }
      qbProperties.push(resultField);
      qbFields[field.sid] = resultField;
    });
  }
}
